import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getJwtPayload, isSuperAdminRole } from '../utils/auth';
import { ALL_PRIVILEGE_KEYS } from '../utils/privilegeCatalog';

/**
 * Privilege helper: can(key), canAny([...]), canAll([...]).
 * super_admin always returns true for every key (no view/action restrictions).
 */
export function usePrivilege() {
  const user = useSelector((state) => state.auth?.user);
  const role = user?.role || getJwtPayload()?.role;
  const privileges = user?.privileges;
  const isSuperAdmin = isSuperAdminRole(role);

  const can = useCallback(
    (key) => {
      if (!key) return true;
      // Super admin: unrestricted for all views and actions
      if (isSuperAdmin) return true;
      if (!privileges || typeof privileges !== 'object') return false;
      return Boolean(privileges[key]);
    },
    [isSuperAdmin, privileges]
  );

  const canAny = useCallback(
    (keys = []) => keys.some((k) => can(k)),
    [can]
  );

  const canAll = useCallback(
    (keys = []) => keys.every((k) => can(k)),
    [can]
  );

  const effectivePrivileges = useMemo(() => {
    if (isSuperAdmin) {
      const all = {};
      ALL_PRIVILEGE_KEYS.forEach((k) => {
        all[k] = true;
      });
      return all;
    }
    return privileges || {};
  }, [isSuperAdmin, privileges]);

  return useMemo(
    () => ({ can, canAny, canAll, role, privileges: effectivePrivileges, isSuperAdmin }),
    [can, canAny, canAll, role, effectivePrivileges, isSuperAdmin]
  );
}

export default usePrivilege;
